/**
 * TrainerScorecardPage — /training/trainer-scorecard
 *
 * Trainer Performance Score (TPS) — weighted composite of 6 metrics.
 * Auto: ATT (attendance %), CCT (course completion %), SSH (load vs org-median).
 * Manual: TDQ, SFS (ratings out of 5), RAS (% from assessments).
 *
 * Layout:
 *   • Period selector (month/quarter/year) + period dropdown
 *   • Stat strip: trainers indexed, complete count, weights, grading
 *   • Leaderboard table sorted by TPS (then partial)
 *   • Click row → slide-in drill-down: per-batch breakdown + 3-period trend + manual-input form
 */
import { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import {
  Trophy, Calendar, RefreshCw, AlertTriangle, ChevronRight, Save, X, TrendingUp, TrendingDown, Minus, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const GRADE_STYLES = {
  Excellent: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Good: 'bg-blue-100 text-blue-800 border-blue-200',
  Average: 'bg-amber-100 text-amber-800 border-amber-200',
  'Needs Improvement': 'bg-rose-100 text-rose-800 border-rose-200',
  'Insufficient Data': 'bg-slate-100 text-slate-600 border-slate-200',
};

function fmt(v, suffix = '') {
  if (v === null || v === undefined) return <span className="text-slate-300">—</span>;
  return `${v}${suffix}`;
}

function buildPeriodOptions(periodType) {
  const today = new Date();
  const opts = [];
  if (periodType === 'month') {
    for (let i = 0; i < 12; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString('en', { month: 'short', year: 'numeric' });
      opts.push({ key, label });
    }
  } else if (periodType === 'quarter') {
    let y = today.getFullYear(), q = Math.floor(today.getMonth() / 3) + 1;
    for (let i = 0; i < 8; i++) {
      opts.push({ key: `${y}-Q${q}`, label: `Q${q} ${y}` });
      q -= 1;
      if (q < 1) { q = 4; y -= 1; }
    }
  } else if (periodType === 'year') {
    for (let i = 0; i < 4; i++) {
      const y = today.getFullYear() - i;
      opts.push({ key: String(y), label: String(y) });
    }
  }
  return opts;
}

export default function TrainerScorecardPage() {
  const [periodType, setPeriodType] = useState('month');
  const [periodOptions, setPeriodOptions] = useState(buildPeriodOptions('month'));
  const [periodKey, setPeriodKey] = useState(periodOptions[0].key);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drillTrainer, setDrillTrainer] = useState(null);

  useEffect(() => {
    const opts = buildPeriodOptions(periodType);
    setPeriodOptions(opts);
    setPeriodKey(opts[0].key);
  }, [periodType]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/trainer-scorecard', { params: { period_type: periodType, period_key: periodKey } });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load scorecard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [periodType, periodKey]);

  const stats = useMemo(() => {
    if (!data) return null;
    const tpsList = data.rows.map(r => r.tps).filter(v => v !== null);
    const avg = tpsList.length ? Math.round(tpsList.reduce((a, b) => a + b, 0) / tpsList.length * 10) / 10 : null;
    const top = data.rows.find(r => r.tps !== null);
    return { avg, top };
  }, [data]);

  return (
    <div className="p-6 space-y-4" data-testid="trainer-scorecard-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center shadow">
            <Trophy className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Trainer Scorecard</h1>
            <p className="text-sm text-slate-500">
              Performance Score (TPS) — weighted composite of 6 metrics. Auto-computed from attendance, syllabus & load; manual inputs for delivery / feedback / assessment.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="trainer-scorecard-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Period selector */}
      <Card className="p-3 flex items-center gap-3 flex-wrap">
        <Calendar className="w-4 h-4 text-slate-500" />
        <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Period:</span>
        <Select value={periodType} onValueChange={setPeriodType}>
          <SelectTrigger className="w-[140px] h-9" data-testid="trainer-scorecard-period-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Monthly</SelectItem>
            <SelectItem value="quarter">Quarterly</SelectItem>
            <SelectItem value="year">Yearly</SelectItem>
          </SelectContent>
        </Select>
        <Select value={periodKey} onValueChange={setPeriodKey}>
          <SelectTrigger className="w-[180px] h-9" data-testid="trainer-scorecard-period-key">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {periodOptions.map(o => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {data && (
          <span className="ml-auto flex items-center gap-3 text-xs text-slate-500">
            <span><strong className="text-slate-700">{data.complete_count}</strong> of {data.trainer_count} fully scored</span>
            {stats?.avg !== null && stats?.avg !== undefined && (
              <span>Avg TPS: <strong className="text-slate-700">{stats.avg}</strong></span>
            )}
            {stats?.top && stats.top.tps !== null && (
              <span>🏆 Top: <strong className="text-slate-700">{stats.top.trainer_name.split('.')[0]}</strong> ({stats.top.tps})</span>
            )}
          </span>
        )}
      </Card>

      {/* Weight + grading info */}
      <Card className="p-3 bg-slate-50/60 border-slate-200">
        <div className="flex items-start gap-3 text-[11px]">
          <Info className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
          <div className="space-y-1.5 flex-1">
            <p>
              <strong>Weights:</strong> TDQ 25% · SFS 20% · ATT 15% · CCT 15% · SSH 10% · RAS 15%
            </p>
            <p>
              <strong>Grading:</strong>{' '}
              <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 mr-1">🏆 Excellent ≥90</span>
              <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 mr-1">✅ Good 75–89</span>
              <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 mr-1">⚠️ Average 60–74</span>
              <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">🔴 Needs Improvement &lt;60</span>
            </p>
          </div>
        </div>
      </Card>

      {/* Leaderboard */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="trainer-scorecard-table">
            <thead className="bg-slate-50 border-b text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">#</th>
                <th className="text-left px-3 py-2 font-semibold">Trainer</th>
                <th className="text-right px-3 py-2 font-semibold">TPS</th>
                <th className="text-left px-3 py-2 font-semibold">Grade</th>
                <th className="text-right px-3 py-2 font-semibold" title="Training Delivery Quality (manual /5)">TDQ</th>
                <th className="text-right px-3 py-2 font-semibold" title="Student Feedback Score (manual /5)">SFS</th>
                <th className="text-right px-3 py-2 font-semibold" title="Attendance % (auto)">ATT</th>
                <th className="text-right px-3 py-2 font-semibold" title="Course Completion Timeliness % (auto)">CCT</th>
                <th className="text-right px-3 py-2 font-semibold" title="Student Strength Handling (auto)">SSH</th>
                <th className="text-right px-3 py-2 font-semibold" title="Review/Assessment Score % (manual)">RAS</th>
                <th className="text-left px-3 py-2 font-semibold">Flags</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {!data && (
                <tr><td colSpan={12} className="text-center py-12 text-slate-400 text-sm">{loading ? 'Loading…' : 'No data'}</td></tr>
              )}
              {data?.rows?.length === 0 && (
                <tr><td colSpan={12} className="text-center py-12 text-slate-400 text-sm">No trainers found</td></tr>
              )}
              {data?.rows?.map((r, i) => {
                const m = r.metrics;
                const grade = r.grade?.label || 'Insufficient Data';
                return (
                  <tr key={r.trainer_name + i} className="border-b hover:bg-violet-50/40 cursor-pointer transition-colors"
                      onClick={() => setDrillTrainer(r)}
                      data-testid={`trainer-scorecard-row-${i}`}>
                    <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                    <td className="px-3 py-2 font-semibold text-slate-800">{r.trainer_name}</td>
                    <td className="px-3 py-2 text-right font-bold tabular-nums">
                      {r.tps !== null
                        ? <span className="text-slate-800">{r.tps}</span>
                        : <span className="text-slate-400 italic">{r.partial_score ?? '—'}*</span>}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`${GRADE_STYLES[grade] || ''} text-[10px] font-semibold`}>
                        {r.grade?.emoji} {grade}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(m.TDQ, '%')}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(m.SFS, '%')}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(m.ATT, '%')}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(m.CCT, '%')}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(m.SSH, '%')}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(m.RAS, '%')}</td>
                    <td className="px-3 py-2">
                      {r.flags?.length > 0 ? (
                        <span className="inline-flex items-center gap-1 text-rose-600 text-[10px]" title={r.flags.join(' · ')}>
                          <AlertTriangle className="w-3 h-3" /> {r.flags.length}
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {data?.rows?.some(r => r.tps === null) && (
          <p className="px-3 py-2 text-[10px] text-slate-400 italic border-t bg-slate-50/40">
            * Partial score shown when TDQ / SFS / RAS not yet entered. Click a row to add manual inputs.
          </p>
        )}
      </Card>

      <DrillDownPanel
        trainer={drillTrainer}
        periodType={periodType}
        periodKey={periodKey}
        onClose={() => setDrillTrainer(null)}
        onSaved={load}
      />
    </div>
  );
}

function DrillDownPanel({ trainer, periodType, periodKey, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const open = Boolean(trainer);

  useEffect(() => {
    if (!trainer || !trainer.trainer_id) { setDetail(null); return; }
    setLoading(true);
    api.get(`/training/trainer-scorecard/trainer/${trainer.trainer_id}`, {
      params: { period_type: periodType, period_key: periodKey },
    })
      .then(r => setDetail(r.data))
      .catch(err => toast.error(err?.response?.data?.detail || 'Failed to load detail'))
      .finally(() => setLoading(false));
  }, [trainer, periodType, periodKey]);

  return (
    <Sheet open={open} onOpenChange={v => !v && onClose()}>
      <SheetContent className="w-[640px] sm:max-w-[640px] overflow-y-auto p-0" data-testid="trainer-scorecard-drilldown">
        <SheetHeader className="px-5 py-4 border-b bg-gradient-to-r from-amber-500 to-orange-600 text-white">
          <SheetTitle className="text-white text-lg flex items-center gap-2">
            <Trophy className="w-5 h-5" /> {trainer?.trainer_name || ''}
          </SheetTitle>
          <p className="text-xs opacity-90">
            {periodType === 'month' ? 'Month' : periodType === 'quarter' ? 'Quarter' : 'Year'}: <strong>{periodKey}</strong>
          </p>
        </SheetHeader>

        {loading && <div className="p-8 text-center text-sm text-slate-400">Loading…</div>}

        {detail && (
          <div className="p-5 space-y-5">
            {/* Aggregate score */}
            <Card className="p-4">
              <div className="flex items-baseline justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Aggregate TPS</p>
                <Badge variant="outline" className={GRADE_STYLES[detail.aggregate.grade.label] || ''}>
                  {detail.aggregate.grade.emoji} {detail.aggregate.grade.label}
                </Badge>
              </div>
              <div className="flex items-baseline gap-3">
                <span className="text-4xl font-bold text-slate-800 tabular-nums">
                  {detail.aggregate.tps ?? <span className="text-slate-300 italic text-2xl">{detail.aggregate.partial_score ?? '—'}*</span>}
                </span>
                <span className="text-slate-400 text-sm">/ 100</span>
              </div>
              {!detail.aggregate.have_all_inputs && (
                <p className="text-[10px] text-amber-700 italic mt-1">
                  * Partial score — some manual inputs missing. Use the form below to complete.
                </p>
              )}
              {detail.aggregate.flags?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {detail.aggregate.flags.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200 text-[10px]">
                      <AlertTriangle className="w-3 h-3" /> {f}
                    </span>
                  ))}
                </div>
              )}
            </Card>

            {/* Breakdown chart */}
            <Card className="p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Metric Contribution</p>
              <div className="space-y-1.5">
                {detail.aggregate.breakdown.map(b => {
                  const pct = b.value === null ? 0 : (b.value / 100);
                  const max = (b.weight * 100).toFixed(0);
                  return (
                    <div key={b.metric} className="flex items-center gap-2 text-[11px]">
                      <span className="w-10 font-mono text-slate-500">{b.metric}</span>
                      <div className="flex-1 h-3 rounded bg-slate-100 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-violet-400 to-violet-600"
                             style={{ width: `${pct * 100}%` }} />
                      </div>
                      <span className="w-12 text-right tabular-nums text-slate-600">
                        {b.value === null ? <span className="text-slate-300">—</span> : `${b.value}`}
                      </span>
                      <span className="w-12 text-right tabular-nums text-slate-400">/ {max}</span>
                      <span className="w-10 text-right tabular-nums text-violet-700 font-semibold">
                        {b.contribution !== null ? `+${b.contribution}` : '·'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Trend */}
            {detail.trend?.length > 0 && (
              <Card className="p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">3-Period Trend</p>
                <div className="flex items-end justify-between gap-3">
                  {detail.trend.map((t, i) => {
                    const v = t.tps ?? t.partial ?? 0;
                    const prev = i > 0 ? (detail.trend[i - 1].tps ?? detail.trend[i - 1].partial ?? 0) : v;
                    const diff = v - prev;
                    const Trend = i === 0 ? Minus : diff > 1 ? TrendingUp : diff < -1 ? TrendingDown : Minus;
                    const color = i === 0 ? 'text-slate-400' : diff > 1 ? 'text-emerald-600' : diff < -1 ? 'text-rose-600' : 'text-slate-400';
                    return (
                      <div key={t.period_key} className="flex-1 text-center">
                        <div className="h-20 bg-slate-100 rounded relative mb-1 overflow-hidden">
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-violet-500 to-violet-300"
                               style={{ height: `${Math.min(v, 100)}%` }} />
                        </div>
                        <p className="text-xs font-semibold tabular-nums">{Math.round(v)}{t.tps === null ? '*' : ''}</p>
                        <p className="text-[10px] text-slate-500">{t.period_key}</p>
                        {i > 0 && (
                          <Trend className={`w-3 h-3 mx-auto mt-0.5 ${color}`} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Manual input form */}
            <ManualInputForm trainer={trainer} periodType={periodType} periodKey={periodKey} onSaved={() => { onSaved?.(); }} />

            {/* Per-batch breakdown */}
            {detail.per_batch?.length > 0 && (
              <Card className="p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">
                  Per-Batch Breakdown ({detail.per_batch.length})
                </p>
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {detail.per_batch.map(p => (
                    <div key={p.batch?.id} className="p-2 border border-slate-200 rounded text-[11px] hover:border-violet-300">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800 truncate">{p.batch?.name || '(unknown batch)'}</p>
                          <p className="text-slate-500">{p.batch?.course_name} · {p.batch?.centre_name} · {p.batch?.training_mode}</p>
                        </div>
                        <Badge variant="outline" className={`${GRADE_STYLES[p.grade.label]} text-[10px] flex-shrink-0`}>
                          {p.grade.emoji} {p.tps ?? p.partial_score ?? '—'}
                        </Badge>
                      </div>
                      <div className="flex gap-3 mt-1 text-slate-500 text-[10px]">
                        <span>ATT: {p.metrics.ATT ?? '—'}</span>
                        <span>CCT: {p.metrics.CCT ?? '—'}</span>
                        <span>SSH: {p.metrics.SSH ?? '—'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ManualInputForm({ trainer, periodType, periodKey, onSaved }) {
  const [tdq, setTdq] = useState('');
  const [sfs, setSfs] = useState('');
  const [ras, setRas] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Try to load existing
    if (!trainer) return;
    api.get('/training/trainer-scorecard/manual-input', {
      params: { trainer_name: trainer.trainer_name, period_type: periodType, period_key: periodKey },
    }).then(r => {
      const row = r.data?.rows?.[0];
      setTdq(row?.tdq ?? '');
      setSfs(row?.sfs ?? '');
      setRas(row?.ras ?? '');
      setNotes(row?.notes ?? '');
    }).catch(() => { /* ignore */ });
  }, [trainer, periodType, periodKey]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/training/trainer-scorecard/manual-input', {
        trainer_id: trainer.trainer_id,
        trainer_name: trainer.trainer_name,
        period_type: periodType,
        period_key: periodKey,
        tdq: tdq === '' ? null : Number(tdq),
        sfs: sfs === '' ? null : Number(sfs),
        ras: ras === '' ? null : Number(ras),
        notes: notes || null,
      });
      toast.success('Scores saved — reloading');
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Clear all manual scores for this trainer/period?')) return;
    try {
      await api.delete('/training/trainer-scorecard/manual-input', {
        params: { trainer_name: trainer.trainer_name, period_type: periodType, period_key: periodKey },
      });
      setTdq(''); setSfs(''); setRas(''); setNotes('');
      toast.success('Cleared');
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Clear failed');
    }
  };

  return (
    <Card className="p-4 bg-violet-50/30 border-violet-100">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold">Manual Inputs</p>
        <span className="text-[9px] text-slate-400 italic">Admin / Manager only</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div>
          <label className="text-[10px] text-slate-600 font-semibold block mb-0.5">TDQ <span className="text-slate-400 font-normal">(0–5)</span></label>
          <Input type="number" step="0.1" min="0" max="5" value={tdq} onChange={e => setTdq(e.target.value)} className="h-8 text-xs" data-testid="trainer-scorecard-tdq" />
        </div>
        <div>
          <label className="text-[10px] text-slate-600 font-semibold block mb-0.5">SFS <span className="text-slate-400 font-normal">(0–5)</span></label>
          <Input type="number" step="0.1" min="0" max="5" value={sfs} onChange={e => setSfs(e.target.value)} className="h-8 text-xs" data-testid="trainer-scorecard-sfs" />
        </div>
        <div>
          <label className="text-[10px] text-slate-600 font-semibold block mb-0.5">RAS <span className="text-slate-400 font-normal">(0–100%)</span></label>
          <Input type="number" step="1" min="0" max="100" value={ras} onChange={e => setRas(e.target.value)} className="h-8 text-xs" data-testid="trainer-scorecard-ras" />
        </div>
      </div>
      <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes" className="h-8 text-xs mb-2" />
      <div className="flex gap-2">
        <Button onClick={save} disabled={saving} size="sm" className="bg-violet-600 hover:bg-violet-700 flex-1" data-testid="trainer-scorecard-save">
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : 'Save Scores'}
        </Button>
        <Button onClick={clear} variant="outline" size="sm" data-testid="trainer-scorecard-clear">
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
    </Card>
  );
}
